import React, { useCallback, useMemo, useState } from 'react';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { useApplyBlackoutDates } from '~/hooks/apis/stopsell/useApplyBlackoutDates';
import { useBlackoutDatesByRoomTypes } from '~/hooks/apis/stopsell/useBlackoutDatesByRoomTypes';
import { useExtractStopsellDates } from '~/hooks/apis/stopsell/useExtractStopsellDates';

import { CatalogRoom, ResolvedRoomMatch, StopsellApplyPayload, StopsellExtraction } from '~/types/stopsell';

import StopsellDocumentUploadZone from './StopsellDocumentUploadZone';
import StopsellReviewModal from './StopsellReviewModal';
import StopsellRoomMatchModal from './StopsellRoomMatchModal';

interface Props {
  propertyId: string;
  roomTypes: Array<App.RoomType>;
}

export default function StopsellManagementPane({ propertyId, roomTypes }: Props) {
  const { mutate, isPending, error } = useExtractStopsellDates();
  const { mutate: applyBlackoutDates, isPending: isApplying } = useApplyBlackoutDates(propertyId);
  const [extractionResult, setExtractionResult] = useState<StopsellExtraction | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [matchOpen, setMatchOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);

  const {
    isLoading: isBlackoutLoading,
    isError: isBlackoutError,
    byRoomType: blackoutsByRoomType,
  } = useBlackoutDatesByRoomTypes(propertyId, roomTypes);

  const catalogRooms = useMemo<Array<CatalogRoom>>(
    () =>
      roomTypes.map((r) => ({
        id: r.id,
        name: r.name,
        blackoutDates: blackoutsByRoomType[r.id] ?? [],
      })),
    [roomTypes, blackoutsByRoomType],
  );

  const handleSelectFile = useCallback(
    (file: File) => {
      setFileName(file.name);
      mutate(
        { file, catalogRooms },
        {
          onSuccess: (data: StopsellExtraction) => {
            setExtractionResult(data);
            setMatchOpen(true);
          },
        },
      );
    },
    [mutate, catalogRooms],
  );

  const handleMatchContinue = useCallback(
    (resolvedRooms: Array<ResolvedRoomMatch>) => {
      if (!extractionResult) return;

      const resolvedExtraction: StopsellExtraction = {
        rooms: extractionResult.rooms.map((room, index) => ({
          ...room,
          roomTypeId: resolvedRooms[index]?.catalogRoomId ?? room.roomTypeId,
        })),
      };

      setExtractionResult(resolvedExtraction);
      setMatchOpen(false);
      setReviewOpen(true);
    },
    [extractionResult],
  );

  const handleCloseMatch = useCallback(() => setMatchOpen(false), []);

  const handleCloseReview = useCallback(() => setReviewOpen(false), []);

  const handleApply = useCallback(
    (payload: StopsellApplyPayload) => {
      applyBlackoutDates(payload, { onSuccess: handleCloseReview });
    },
    [applyBlackoutDates, handleCloseReview],
  );

  return (
    <>
      <PageSubheader title="Stopsell Management" />
      <StopsellDocumentUploadZone onSelectFile={handleSelectFile} isPending={isPending} error={error?.message} />

      {extractionResult && (
        <>
          <StopsellRoomMatchModal
            key={fileName}
            open={matchOpen}
            onClose={handleCloseMatch}
            onContinue={handleMatchContinue}
            extraction={extractionResult}
            catalogRooms={catalogRooms}
            fileName={fileName}
            isBlackoutLoading={isBlackoutLoading}
          />

          <StopsellReviewModal
            open={reviewOpen}
            onClose={handleCloseReview}
            onApply={handleApply}
            extraction={extractionResult}
            fileName={fileName}
            catalogRooms={catalogRooms}
            isBlackoutError={isBlackoutError}
            isApplying={isApplying}
          />
        </>
      )}
    </>
  );
}
